import { useEffect, useState } from 'react'

/**
 * A picture that may not be there yet (1.18.0, from use).
 *
 * A video's poster goes up **after** the video: `uploadVideo()` waits a
 * second and a half for it and then lets it follow in the background
 * (`lib/video.ts`). A card drawn in that gap asks `/poster`, gets a 404 --
 * and a plain `<img>` never asks again. From use: "das Vorschaubild auf dem
 * Kärtchen wird erst nach Verzögerung bzw. nach Aufruf des Highlights
 * angezeigt."
 *
 * So this one asks again: after a failure it waits, doubles the wait, and
 * tries once more with a fresh address (`?v=n`, so that no cache answers
 * for the server), six times in all -- about a minute. A poster that never
 * comes (a video the device could not decode) ends as before: an empty
 * frame, not a broken picture.
 */
const WARTEN_MS = [1000, 2000, 4000, 8000, 16000, 16000]

export function Standbild({
  src,
  className,
  loading,
}: {
  src: string
  className?: string
  loading?: 'lazy' | 'eager'
}) {
  const [versuch, setVersuch] = useState(0)
  const [aufgegeben, setAufgegeben] = useState(false)

  // A new address starts over.
  useEffect(() => {
    setVersuch(0)
    setAufgegeben(false)
  }, [src])

  function fehlgeschlagen() {
    const warten = WARTEN_MS[versuch]
    if (warten === undefined) {
      setAufgegeben(true)
      return
    }
    window.setTimeout(() => setVersuch((n) => n + 1), warten)
  }

  const adresse = versuch === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}v=${versuch}`
  return (
    <img
      key={adresse}
      className={aufgegeben ? `${className ?? ''} standbild--fehlt`.trim() : className}
      src={adresse}
      alt=""
      loading={loading}
      onError={fehlgeschlagen}
      data-standbild-versuch={versuch}
    />
  )
}
